import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Globe2, ArrowLeft, Users } from "lucide-react";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../lib/auth";
import { usePublicConfig } from "../lib/publicConfig";
import FeedComposer from "../components/FeedComposer";
import FeedCard from "../components/FeedCard";

const TABS = [
  { key: "latest", label: "Latest" },
  { key: "top",    label: "Top" },
  { key: "unread", label: "Unread" },
];

export default function Community() {
  const { user } = useAuth();
  const cfg = usePublicConfig();
  const isAdmin = user?.role === "super_admin";
  const myAlias = user?.public_alias;
  // Phase 20 — Community kill-switch. Non-admins see a friendly empty state
  // instead of 404/403 when the flag is off. Admins bypass and see the feed.
  const isDisabled = !cfg.community_enabled && !isAdmin;

  const [searchParams, setSearchParams] = useSearchParams();
  const expandedPostId = searchParams.get("post");
  const tab = searchParams.get("sort") || "latest";
  const period = searchParams.get("period") || (tab === "top" ? "30d" : "all");

  const [posts, setPosts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tagFilter, setTagFilter] = useState("");
  const [courses, setCourses] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/community/posts?sort=${tab}&period=${period}`);
      setPosts(r.data.posts || []);
      setUnreadCount(r.data.unread_count || 0);
    } catch (e) { console.error("community load", e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (isDisabled) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, period, isDisabled]);

  useEffect(() => {
    if (isDisabled) return;
    api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
  }, [isDisabled]);

  const setTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("sort", nextTab);
    if (nextTab === "top") next.set("period", "30d");
    else next.delete("period");
    next.delete("post");
    setSearchParams(next, { replace: true });
  };
  const setExpanded = (postId, on) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set("post", postId); else next.delete("post");
    setSearchParams(next, { replace: true });
  };

  const visible = useMemo(() => {
    let out = posts.filter(p => !p._deleted);
    if (tagFilter) out = out.filter(p => p.course_tag === tagFilter);
    // Phase 13.2 fix — defensive de-dupe by id. Guards against StrictMode
    // double-mount fetches and any nested setPosts bubble that could
    // accidentally surface the same post twice in the rendered list.
    out = Array.from(new Map(out.map(p => [p.id, p])).values());
    // Pinned first, server already sorted by sort/period
    return [...out].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  }, [posts, tagFilter]);

  // Phase 14.5 — Topic pills are LIVE-ONLY for standard members. Coming-soon
  // and unpublished courses shouldn't seed community topic filters (nothing
  // to discuss yet). Admins see all so they can seed early conversations.
  const topicCourses = useMemo(() => {
    if (isAdmin) return courses.slice(0, 12);
    return courses
      .filter(c => c.is_published !== false && !c.is_coming_soon)
      .slice(0, 12);
  }, [courses, isAdmin]);

  const onPostChange = (next) => {
    setPosts(ps => ps.map(p => p.id === next.id ? next : p).filter(p => !(p._deleted && p.id === next.id)));
    if (next._deleted) setPosts(ps => ps.filter(p => p.id !== next.id));
  };

  // Scroll the expanded card into view on mount
  useEffect(() => {
    if (!expandedPostId || loading) return;
    const el = document.querySelector(`[data-testid="feed-card-${expandedPostId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [expandedPostId, loading]);

  if (isDisabled) {
    return (
      <div className="min-h-screen bg-[#F0F1F5] text-fc-navy flex" data-testid="community-page">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center p-10" data-testid="community-disabled">
          <div className="max-w-md text-center">
            <div className="inline-flex w-14 h-14 rounded-full bg-fc-red/10 items-center justify-center mb-5">
              <Users className="text-fc-red" size={22} />
            </div>
            <h1 className="text-2xl font-black text-fc-navy mb-3">Community is coming soon.</h1>
            <p className="text-fc-medium-gray text-sm leading-relaxed mb-6">
              We&apos;re finalizing the public community feed. In the meantime, keep the conversations going inside your organization via the team feed and DMs.
            </p>
            <Link to="/dashboard" className="btn-fc-red inline-flex items-center gap-2" data-testid="community-disabled-back">
              Back to dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F1F5] text-fc-navy flex" data-testid="community-page">
      <Sidebar />
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">
        {expandedPostId && (
          <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40 lg:hidden">
            <button onClick={() => setExpanded(null, false)} className="px-4 py-2.5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-fc-red" data-testid="back-to-feed">
              <ArrowLeft size={13} /> Back to feed
            </button>
          </div>
        )}
        {/* Top strip */}
        <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
          <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase font-black text-[#6B7280]">COMMUNITY</p>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-fc-navy">Global feed</h1>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-gray-50 border border-[#E5E7EB] rounded-full px-3 py-1.5 text-xs font-bold text-fc-navy" data-testid="community-scope-pill">
              <Globe2 size={12} /> Global Community
            </div>
          </div>
          <div className="max-w-[760px] mx-auto px-4 sm:px-6">
            <div className="flex gap-1 border-b border-[#E5E7EB] -mx-1">
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`relative px-4 py-2.5 text-[13px] font-bold tracking-wide border-b-2 transition-colors ${
                    tab === t.key ? "border-fc-red text-fc-red" : "border-transparent text-[#6B7280] hover:text-fc-navy"
                  }`}
                  data-testid={`feed-tab-${t.key}`}
                >
                  {t.label}
                  {t.key === "unread" && unreadCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-fc-red text-white text-[10px] rounded-full font-black" data-testid="unread-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          {topicCourses.length > 0 && (
            <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="community-topic-pills">
              <button
                onClick={() => setTagFilter("")}
                className={`text-[11px] tracking-[0.14em] uppercase font-bold whitespace-nowrap px-3 py-1.5 rounded-full border transition-colors ${
                  tagFilter === "" ? "bg-fc-navy text-white border-fc-navy" : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-fc-navy"
                }`}
                data-testid="topic-pill-all"
              >
                All
              </button>
              {topicCourses.map(c => (
                <button
                  key={c.slug}
                  onClick={() => setTagFilter(c.slug)}
                  className={`text-[11px] tracking-[0.14em] uppercase font-bold whitespace-nowrap px-3 py-1.5 rounded-full border transition-colors ${
                    tagFilter === c.slug ? "bg-fc-red text-white border-fc-red" : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-fc-red"
                  }`}
                  data-testid={`topic-pill-${c.slug}`}
                >
                  {c.title.length > 24 ? c.title.slice(0, 24) + "…" : c.title}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8 sm:space-y-12 pb-32">
          <FeedComposer scope="community" onPosted={load} />

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : visible.length === 0 ? (
            <EmptyState tab={tab} />
          ) : (
            visible.map(p => (
              <FeedCard
                key={p.id} post={p} scope="community"
                currentAlias={myAlias} isAdmin={isAdmin} onChange={onPostChange}
                expanded={p.id === expandedPostId}
                onToggleExpanded={(open) => setExpanded(p.id, open)}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-32 bg-gray-200 rounded" />
          <div className="h-2 w-24 bg-gray-100 rounded" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-4 w-3/4 bg-gray-200 rounded" />
        <div className="h-4 w-full bg-gray-100 rounded" />
        <div className="h-4 w-5/6 bg-gray-100 rounded" />
      </div>
    </div>
  );
}

function EmptyState({ tab }) {
  const isUnread = tab === "unread";
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center" data-testid="feed-empty">
      <div className="w-16 h-16 rounded-full bg-fc-red/10 mx-auto mb-4 flex items-center justify-center">
        <Globe2 className="text-fc-red" size={24} />
      </div>
      <h3 className="text-lg font-black tracking-tight text-fc-navy mb-2">
        {isUnread ? "You're all caught up." : "Quiet in here."}
      </h3>
      <p className="text-[#6B7280] text-sm">
        {isUnread ? "Nothing new to read. Switch to Latest to see all posts." :
                    "Be the first to post — share what you built, learned, or broke this week."}
      </p>
    </div>
  );
}
